import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

// The store stays free of qampo-ui-library: importing that library at runtime
// pulls in its module side effects, which break the node test environment. The
// view layer (App.tsx) maps these plain values onto the library's ToastData.
export type ToastVariant = 'notification' | 'warning' | 'error';
export type ToastPosition = 'top' | 'bottom';

export interface AppToast {
  id: string;
  message: string;
  variant: ToastVariant;
  header?: string;
  autoDismiss: boolean;
  position: ToastPosition;
}

interface ToastState {
  toasts: AppToast[];
}

const initialState: ToastState = {
  toasts: []
};

interface ShowToastPayload {
  message: string;
  variant?: ToastVariant;
  header?: string;
  autoDismiss?: boolean;
  position?: ToastPosition;
}

const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    showToast: {
      reducer(state, action: PayloadAction<AppToast>) {
        state.toasts.push(action.payload);
      },
      prepare(payload: ShowToastPayload) {
        const toast: AppToast = {
          id: nanoid(),
          message: payload.message,
          variant: payload.variant ?? 'notification',
          header: payload.header,
          autoDismiss: payload.autoDismiss ?? true,
          position: payload.position ?? 'bottom'
        };
        return { payload: toast };
      }
    },
    dismissToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    }
  }
});

export const { showToast, dismissToast } = toastSlice.actions;

export const selectToasts = (state: RootState) => state.toastSlice.toasts;

export default toastSlice.reducer;
